<template>
  <div class="cart--empty" v-if="!totalPrice && !calculatePriceError">
    <img src="@/assets/images/common/empty-mailbox.svg" />
    <p class="small">{{ $t('W2P_EMPTY_CART_TEXT') }}</p>
    <div class="cart--loading" v-if="isLoading">
      <pn-spinner color="blue" size="large" />
    </div>
  </div>
  <div class="cart--empty" v-else-if="calculatePriceError">
    <img src="@/assets/images/common/estimate_letter_grey.svg" />
    <p class="cart__name small">{{ $t('W2P_ERROR_CART_TEXT') }}</p>
    <div class="cart--loading" v-if="isLoading">
      <pn-spinner color="blue" size="large" />
    </div>
  </div>
  <div class="cart" v-else>
    <img src="@/assets/images/common/estimate_letter.svg" />
    <p class="cart__name small">{{ mailoutName }}</p>
    <p class="cart__count tiny">
      {{ recipientsCount }} {{ $t('W2P_CART_RECIPIENTS') }}
    </p>
    <div class="cart__item">
      <p class="small">{{ $t('W2P_CART_BASE_PRICE') }}</p>
      <p class="small">{{ $n(basePrice, 'currency', 'sv-SE') }}</p>
    </div>
    <div class="cart__price-per-item">
      <p class="tiny cart__item__name">
        {{ $n(basePricePerPiece, 'currency', 'sv-SE') }}/{{
          $t('W2P_PRODUCT_DEFINITION_PIECE')
        }}
      </p>
    </div>
    <div v-for="extra in extras" :key="extra.name">
      <div class="cart__item">
        <p class="small">{{ $t(extra.name) }}</p>
        <p class="small">{{ $n(extra.price, 'currency', 'sv-SE') }}</p>
      </div>
      <div class="cart__price-per-item">
        <p class="tiny cart__item__name">
          {{ $n(extra.pricePerPiece, 'currency', 'sv-SE') }}/{{
            $t('W2P_PRODUCT_DEFINITION_PIECE')
          }}
        </p>
      </div>
    </div>
    <div class="cart__item">
      <p class="small">{{ $t('W2P_CART_POSTAGE_PRICE') }}</p>
      <p class="small">{{ $n(totalPostage, 'currency', 'sv-SE') }}</p>
    </div>
    <div class="cart__price-per-item">
      <p class="tiny cart__item__name">
        {{ $n(postagePricePerPiece, 'currency', 'sv-SE') }}/{{
          $t('W2P_PRODUCT_DEFINITION_PIECE')
        }}
      </p>
    </div>
    <div class="cart__item">
      <p class="small">{{ $t('W2P_CART_TOTAL_EXCL_VAT') }}</p>
      <p class="small">{{ $n(totalPrice, 'currency', 'sv-SE') }}</p>
    </div>
    <hr />
    <div class="cart__item">
      <p class="bold" v-if="isCostEstimate">
        {{ $t('W2P_CART_ESTIMATED_TOTAL_INCL_VAT') }}
      </p>
      <p class="bold" v-else>{{ $t('W2P_CART_TOTAL_INCL_VAT') }}</p>
      <p class="large bold cart__item--blue" v-if="!isLoading">
        {{ $n(totalPriceVAT, 'currency', 'sv-SE') }}
      </p>
      <div v-if="isLoading">
        <pn-spinner color="blue" size="medium" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState, mapMutations } from 'vuex'
import _ from 'lodash'

export default {
  props: {
    service: {
      type: String,
      required: true
    }
  },
  computed: {
    ...mapGetters(['isBusinessUser']),
    ...mapGetters('letter/upload', ['getComputedNumberOfPages']),
    ...mapState('cart', [
      'basePrice',
      'basePricePerPiece',
      'totalExtra',
      'totalPostage',
      'postagePricePerPiece',
      'totalPrice',
      'totalPriceVAT',
      'extras',
      'isLoading',
      'calculatePriceError'
    ]),
    upload() {
      return this.$store.state[this.service].upload
    },
    address() {
      return this.$store.state[this.service].address
    },
    delivery() {
      return this.$store.state[this.service].delivery
    },
    isCostEstimate() {
      return this.upload.isCostEstimate
    },
    mailoutName() {
      return this.upload.mailoutName
    },
    recipientsCount() {
      if (
        !this.isCostEstimate &&
        this.address &&
        this.address.numberOfRecipients
      ) {
        return this.address.numberOfRecipients
      } else {
        return this.upload.recipientsData.recipientsCount
      }
    }
  },
  created() {
    if (this.upload.productId) {
      this.updateCart()
    }

    this.$store.subscribe(mutation => {
      if (
        [
          'letter/upload/UPDATE_LETTER_OPTIONS',
          'letter/upload/SAVE_MATERIAL',
          'letter/upload/SET_COST_ESTIMATE',
          'letter/address/SAVE',
          'letter/delivery/SAVE_DELIVERY_ID'
        ].includes(mutation.type)
      ) {
        this.updateCart()
      }

      if (mutation.type === 'cart/CALCULATE_FAILURE') {
        this.checkCalculateFailure()
      }
    })
  },
  methods: {
    ...mapMutations({
      resetDelivery(commit) {
        return commit('letter/delivery/RESET_STATE_ALL')
      }
    }),
    updateCart: _.debounce(function() {
      let numberOfPages = this.getComputedNumberOfPages
      let numberOfRecipients = this.address.numberOfRecipients || 1
      let recipientsData = this.address.recipientsData || {
        '0': numberOfRecipients
      }
      let deliveryId =
        this.delivery.deliveryId ||
        this.getLowestPriceDeliveryId(numberOfRecipients)

      if (this.upload.isCostEstimate) {
        numberOfPages = this.upload.recipientsData.pageCount
        numberOfRecipients = this.upload.recipientsData.recipientsCount
        recipientsData = { '0': numberOfRecipients }
        deliveryId = this.upload.deliveryId
      }

      const request = {
        addressType: 'FILE',
        country: 'SE',
        deliveryId,
        numberOfPages,
        productId: this.upload.productId,
        recipientsData,
        userType: this.isBusinessUser ? 'company' : 'public',
        numberOfRecipients, //FIXME this is for only one zone only
        language: this.$store.state.lang
      }

      this.$store.dispatch('cart/CALCULATE_PRICE', request)
    }, 1000),
    checkCalculateFailure() {
      if (this.delivery.deliveryId) {
        this.resetDelivery()

        if (this.$router.name !== 'delivery') {
          this.$router.push({ name: 'delivery' })
          this.updateCart()
        }
      }
    },
    getLowestPriceDeliveryId(numberOfRecipients) {
      if (this.isBusinessUser) {
        return numberOfRecipients < 500 ? '100' : '300'
      } else {
        return '500'
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.cart {
  max-width: 17.5em;
  border: 0.1rem solid $gray200;
  height: 100%;
  text-align: center;
  padding: 1em 2em;
  color: $black;
  position: -webkit-sticky; /* Safari */
  position: sticky;
  top: 6em;

  &--loading {
    margin-top: 1em;
  }

  &__name {
    margin-top: 1em;
  }

  &__count {
    color: $gray700;
  }

  &--empty {
    max-width: 28rem;
    border: 0.1rem solid $gray200;
    height: 100%;
    text-align: center;
    padding: 2em 3em;
    color: $gray700;
  }

  &--empty {
    position: -webkit-sticky;
    position: sticky;
    top: 6em;
  }

  &__price-per-item {
    display: flex;
    justify-content: space-between;
    text-align: left;
    line-height: 1em;
  }

  &__item {
    display: flex;
    justify-content: space-between;
    margin-top: 1em;
    text-align: left;
    line-height: 1em;

    &--blue {
      color: $blue700;
    }

    &--subitem {
      margin-left: 1em;
      margin-top: 0.5em;
      display: flex;
      justify-content: space-between;
      text-align: left;
    }

    &__name {
      color: $gray700;
    }
  }
}

hr {
  display: block;
  height: 0.1rem;
  border: 0;
  border-top: 0.1rem solid $gray200;
  margin: 1em 0;
  padding: 0;
}

@media screen and (max-width: 75em) {
  .cart {
    // for now
    display: none;
  }

  .cart--empty {
    display: none;
  }
}
</style>
